import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { simhubAPI, adminSimhubAPI } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ArrowLeft, Megaphone, Tag, Trophy, Users, Plus, Trash2, RefreshCw, Send, Clock } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "");

function AdminPost({ onAdd, label, withLink }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      await onAdd({ title: title.trim(), body: body.trim(), image_url: imageUrl.trim(), link: link.trim() });
      setOpen(false); setTitle(""); setBody(""); setImageUrl(""); setLink("");
    } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`club-add-${label}`}>
        <Plus className="mr-1 h-4 w-4" /> Add {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-border bg-card">
          <DialogHeader><DialogTitle>New {label}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid={`club-${label}-title`} /></div>
            <div className="space-y-1.5"><Label>Body</Label><Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Image URL (optional)</Label><Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." /></div>
            {withLink && <div className="space-y-1.5"><Label>Link (optional)</Label><Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://..." /></div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-primary text-primary-foreground" data-testid={`club-${label}-submit`}>Post</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PostList({ items, isAdmin, onDelete, icon: Icon }) {
  if (!items.length) return <p className="py-8 text-center text-sm text-muted-foreground">Nothing here yet.</p>;
  return (
    <div className="space-y-3">
      {items.map((it) => (
        <Card key={it.id} className="border-border bg-card p-4" data-testid={`club-post-${it.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/15"><Icon className="h-4 w-4 text-primary" /></div>
              <div>
                <p className="font-semibold">{it.title}</p>
                {it.body && <p className="mt-1 text-sm text-muted-foreground whitespace-pre-wrap">{it.body}</p>}
                {it.image_url && <img src={it.image_url} alt={it.title} className="mt-2 max-h-56 rounded-md" />}
                {it.link && <a href={it.link} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-primary underline">Learn more</a>}
                <p className="mt-1 text-[11px] text-muted-foreground">{fmtDate(it.created_at)}</p>
              </div>
            </div>
            {isAdmin && <Button size="sm" variant="ghost" onClick={() => onDelete(it.id)} data-testid={`club-post-delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button>}
          </div>
        </Card>
      ))}
    </div>
  );
}

function CreateTournament({ clubId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", format: "wheel_to_wheel", track_name: "", track_key: "", starts_at: "", ends_at: "", points: "25,18,15,12,10,8,6,4,2,1" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name.trim() || !form.starts_at || !form.ends_at) { toast.error("Name, start and end are required"); return; }
    const pv = {};
    form.points.split(",").map((s) => s.trim()).filter(Boolean).forEach((p, i) => { pv[String(i + 1)] = parseInt(p, 10) || 0; });
    setBusy(true);
    try {
      await simhubAPI.createTournament(clubId, {
        name: form.name.trim(), description: form.description.trim(), format: form.format,
        track_key: form.track_key.trim(), track_name: form.track_name.trim(),
        starts_at: new Date(form.starts_at).toISOString(), ends_at: new Date(form.ends_at).toISOString(),
        point_values: pv,
      });
      toast.success("Tournament created");
      setOpen(false); onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="club-add-tournament"><Plus className="mr-1 h-4 w-4" /> Create tournament</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-border bg-card max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create tournament</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="tournament-name" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label>Format</Label>
              <select className="w-full rounded-md border border-border bg-background p-2 text-sm" value={form.format} onChange={(e) => set("format", e.target.value)} data-testid="tournament-format">
                <option value="wheel_to_wheel">Wheel-to-wheel (finish position)</option>
                <option value="time_attack">Time attack (best lap)</option>
              </select>
            </div>
            <div className="space-y-1.5"><Label>Required track key (optional)</Label><Input value={form.track_key} onChange={(e) => set("track_key", e.target.value)} placeholder="e.g. spa (leave empty = any)" /></div>
            <div className="space-y-1.5"><Label>Track name (display)</Label><Input value={form.track_name} onChange={(e) => set("track_name", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Starts</Label><Input type="datetime-local" value={form.starts_at} onChange={(e) => set("starts_at", e.target.value)} data-testid="tournament-starts" /></div>
              <div className="space-y-1.5"><Label>Ends</Label><Input type="datetime-local" value={form.ends_at} onChange={(e) => set("ends_at", e.target.value)} data-testid="tournament-ends" /></div>
            </div>
            <div className="space-y-1.5"><Label>Points per position (P1,P2,...)</Label><Input value={form.points} onChange={(e) => set("points", e.target.value)} data-testid="tournament-points" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-primary text-primary-foreground" data-testid="tournament-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TournamentCard({ t, isAdmin, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const loadDetail = useCallback(async () => {
    try { const r = await simhubAPI.tournamentDetail(t.id); setDetail(r.data); } catch { /* ignore */ }
  }, [t.id]);
  useEffect(() => { if (open) loadDetail(); }, [open, loadDetail]);

  const join = async () => { try { await simhubAPI.joinTournament(t.id); toast.success("Joined"); onChanged(); loadDetail(); } catch (e) { toast.error(e.response?.data?.detail || "Could not join"); } };
  const leave = async () => { try { await simhubAPI.leaveTournament(t.id); toast.success("Left"); onChanged(); loadDetail(); } catch { toast.error("Could not leave"); } };
  const sync = async () => { try { const r = await simhubAPI.syncTournament(t.id); toast.success(`Synced ${r.data.matched_sessions} sessions`); setDetail((d) => ({ ...d, standings: r.data.standings })); } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); } };
  const sendMsg = async () => { if (!msg.trim()) return; try { const r = await simhubAPI.messageTournament(t.id, { text: msg.trim() }); toast.success(`Sent to ${r.data.sent}`); setMsg(""); } catch { toast.error("Could not send"); } };

  return (
    <Card className="border-border bg-card p-4" data-testid={`tournament-card-${t.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-primary" />
            <p className="font-semibold">{t.name}</p>
            <Badge variant="secondary" className="text-[10px] capitalize">{t.format === "time_attack" ? "Time attack" : "Wheel to wheel"}</Badge>
          </div>
          {t.description && <p className="mt-1 text-sm text-muted-foreground">{t.description}</p>}
          <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" /> {fmtDate(t.starts_at)} → {fmtDate(t.ends_at)}{t.track_name ? ` · ${t.track_name}` : ""}</p>
          <p className="mt-0.5 text-xs text-muted-foreground"><Users className="mr-1 inline h-3 w-3" />{t.participant_count} joined</p>
        </div>
        <div className="flex flex-col gap-2">
          {t.is_joined
            ? <Button size="sm" variant="outline" onClick={leave} data-testid={`tournament-leave-${t.id}`}>Leave</Button>
            : <Button size="sm" className="bg-primary text-primary-foreground" onClick={join} data-testid={`tournament-join-${t.id}`}>Join</Button>}
          <Button size="sm" variant="ghost" onClick={() => setOpen((o) => !o)} data-testid={`tournament-view-${t.id}`}>{open ? "Hide" : "Standings"}</Button>
          {isAdmin && (
            <Button size="sm" variant="ghost" className="text-destructive" onClick={async () => { if (window.confirm("Delete this tournament?")) { try { await simhubAPI.deleteTournament(t.id); toast.success("Deleted"); onChanged(); } catch { toast.error("Could not delete"); } } }} data-testid={`tournament-delete-${t.id}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {open && (
        <div className="mt-3 border-t border-border pt-3">
          {isAdmin && (
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={sync} data-testid={`tournament-sync-${t.id}`}><RefreshCw className="mr-1 h-4 w-4" /> Sync telemetry</Button>
              <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Message all participants" className="h-8 max-w-xs" data-testid={`tournament-msg-${t.id}`} />
              <Button size="sm" variant="ghost" onClick={sendMsg}><Send className="h-4 w-4" /></Button>
            </div>
          )}
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Standings</p>
          {!detail ? <Skeleton className="h-16 rounded-md" /> : !detail.standings?.length ? (
            <p className="text-sm text-muted-foreground">No results yet. {isAdmin ? "Run Sync telemetry after the window." : "Race during the tournament window to appear here."}</p>
          ) : (
            <div className="space-y-1" data-testid={`tournament-standings-${t.id}`}>
              {detail.standings.map((s) => (
                <div key={s.user_id} className="flex items-center justify-between rounded-md bg-secondary/40 px-3 py-1.5 text-sm">
                  <span><span className="mr-2 font-mono text-muted-foreground">{s.rank}.</span>{s.username || s.user_id}</span>
                  <span className="font-bold text-primary">{s.points} pts</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function ClubPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [tournaments, setTournaments] = useState([]);

  const loadContent = useCallback(async () => {
    const [a, p, t] = await Promise.all([
      simhubAPI.clubAnnouncements(id).catch(() => ({ data: [] })),
      simhubAPI.clubPromotions(id).catch(() => ({ data: [] })),
      simhubAPI.clubTournaments(id).catch(() => ({ data: [] })),
    ]);
    setAnnouncements(a.data || []);
    setPromotions(p.data || []);
    setTournaments(t.data || []);
  }, [id]);

  useEffect(() => {
    simhubAPI.clubPage(id)
      .then((r) => { setClub(r.data); loadContent(); })
      .catch((e) => { if (e.response?.status === 403) setForbidden(true); })
      .finally(() => setLoading(false));
  }, [id, loadContent]);

  if (loading) return <div className="mx-auto max-w-4xl"><Skeleton className="h-48 rounded-xl" /></div>;
  if (forbidden) return (
    <div className="mx-auto max-w-md py-20 text-center">
      <Users className="mx-auto mb-3 h-12 w-12 text-muted-foreground" />
      <p className="font-semibold">Members only</p>
      <p className="mt-1 text-sm text-muted-foreground">Join this club to view its page, announcements and tournaments.</p>
      <Button className="mt-4" onClick={() => navigate("/clubs")} data-testid="club-back-to-clubs">Back to clubs</Button>
    </div>
  );
  if (!club) return <div className="mx-auto max-w-md py-20 text-center"><p>Club not found.</p><Button className="mt-4" onClick={() => navigate("/clubs")}>Back to clubs</Button></div>;

  const isAdmin = club.is_admin;

  const editCover = async () => {
    const url = window.prompt("Cover image URL", club.cover_image || "");
    if (url === null) return;
    try {
      await adminSimhubAPI.updateCustomClub(id, { cover_image: url.trim() });
      setClub((c) => ({ ...c, cover_image: url.trim() }));
      toast.success("Cover updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update cover"); }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <button onClick={() => navigate("/clubs")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="club-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Cover */}
      <section className="relative overflow-hidden rounded-2xl border border-border" data-testid="club-header">
        <div className="h-40 w-full bg-cover bg-center sm:h-52" style={{ backgroundImage: club.cover_image ? `url(${club.cover_image})` : "linear-gradient(135deg, hsl(240 8% 12%), hsl(2 100% 20%))" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        {isAdmin && (
          <Button size="sm" variant="outline" className="absolute right-3 top-3" onClick={editCover} data-testid="club-edit-cover">Edit cover</Button>
        )}
        <div className="absolute bottom-0 p-5">
          <h1 className="font-display text-3xl font-bold uppercase">{club.name}</h1>
          <p className="text-sm text-muted-foreground">{club.member_count} members{club.description ? ` · ${club.description}` : ""}</p>
        </div>
      </section>

      <Tabs defaultValue="announcements">
        <TabsList>
          <TabsTrigger value="announcements" data-testid="club-tab-announcements"><Megaphone className="mr-1.5 h-4 w-4" /> Announcements</TabsTrigger>
          <TabsTrigger value="promotions" data-testid="club-tab-promotions"><Tag className="mr-1.5 h-4 w-4" /> Promotions</TabsTrigger>
          <TabsTrigger value="tournaments" data-testid="club-tab-tournaments"><Trophy className="mr-1.5 h-4 w-4" /> Tournaments</TabsTrigger>
        </TabsList>

        <TabsContent value="announcements" className="mt-4 space-y-3">
          {isAdmin && <AdminPost label="announcement" onAdd={async (d) => { await simhubAPI.createAnnouncement(id, d); toast.success("Posted"); loadContent(); }} />}
          <PostList items={announcements} isAdmin={isAdmin} icon={Megaphone} onDelete={async (iid) => { await simhubAPI.deleteAnnouncement(id, iid); loadContent(); }} />
        </TabsContent>

        <TabsContent value="promotions" className="mt-4 space-y-3">
          {isAdmin && <AdminPost label="promotion" withLink onAdd={async (d) => { await simhubAPI.createPromotion(id, d); toast.success("Posted"); loadContent(); }} />}
          <PostList items={promotions} isAdmin={isAdmin} icon={Tag} onDelete={async (iid) => { await simhubAPI.deletePromotion(id, iid); loadContent(); }} />
        </TabsContent>

        <TabsContent value="tournaments" className="mt-4 space-y-3">
          {isAdmin && <CreateTournament clubId={id} onCreated={loadContent} />}
          {!tournaments.length ? <p className="py-8 text-center text-sm text-muted-foreground">No tournaments yet.</p> : (
            <div className="space-y-3">
              {tournaments.map((t) => <TournamentCard key={t.id} t={t} isAdmin={isAdmin} onChanged={loadContent} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
